
import { LeadFormData, Lead } from '@/types/lead';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/utils/toast';
import { validateLeadForm, sanitizeFormData } from '@/utils/formValidation';
import { resolveTrafficSource } from '@/utils/trafficSource';

export const saveLead = async (formData: any, isEligible: boolean): Promise<Lead | null> => {
  try {
    // Validação final antes de salvar no Supabase
    const basicFormData = {
      name: formData.name || '',
      whatsapp: formData.whatsapp || '',
      cnpj: formData.cnpj || ''
    };
    
    console.log('🔍 Running final validation before saving to Supabase...');
    const validation = validateLeadForm(basicFormData);
    
    if (!validation.isValid) {
      console.error('❌ Final validation failed:', validation.errors);
      toast.error(`Erro de validação: ${validation.errors[0]}`);
      return null;
    }
    
    // Sanitizar dados uma última vez
    const sanitizedBasicData = sanitizeFormData(basicFormData);
    console.log('✅ Data passed final validation and sanitization');
    
    // Resolve traffic source
    const { sourceName, utmData } = resolveTrafficSource();
    console.log('🔍 Resolved traffic source:', sourceName, utmData);

    // Combinar dados sanitizados com dados extras
    const leadData = {
      name: sanitizedBasicData.name,
      whatsapp: sanitizedBasicData.whatsapp,
      email: formData.email || '',
      city: formData.city || '',
      state: formData.state || '',
      cnpj: sanitizedBasicData.cnpj,
      eligible: isEligible,
      tax_regime: formData.taxRegime || 'Não informado',
      emits_invoice: formData.emitsInvoice || false,
      monthly_revenue: formData.monthlyRevenue || 0,
      status: 'Novo lead' as const,
      submission_date: new Date().toISOString(),
      source_name: sourceName,
      utm_data: Object.keys(utmData).length > 0 ? utmData : null,
    };

    console.log('🚀 Attempting to save lead data to Supabase:', leadData);
    console.log('🔍 Lead data types:', {
      name: typeof leadData.name,
      whatsapp: typeof leadData.whatsapp,
      email: typeof leadData.email,
      city: typeof leadData.city,
      state: typeof leadData.state,
      cnpj: typeof leadData.cnpj,
      eligible: typeof leadData.eligible,
      tax_regime: typeof leadData.tax_regime,
      emits_invoice: typeof leadData.emits_invoice,
      monthly_revenue: typeof leadData.monthly_revenue,
      status: typeof leadData.status
    });

    // Insert without selecting to avoid RLS select restrictions; use minimal returning
      const { error } = await (supabase as any)
      .from('taxhealth_leads')
      .insert(leadData, { returning: 'minimal' });

    if (error) {
      console.error('❌ ERROR saving lead to Supabase database:', error);
      console.error('❌ Error details:', {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code
      });
      
      // Try to save locally as fallback
      console.log('💾 Attempting to save lead locally as fallback');
      const { saveLead: saveLeadToLocalStorage } = await import('@/utils/localStorage');
      await saveLeadToLocalStorage(formData);
      
      toast.error('⚠️ Dados salvos localmente - erro de conexão com servidor');
      
      // Return a constructed lead object to ensure the process continues
      return {
        ...leadData,
        id: 'local-' + Date.now(),
        notes: 'Lead saved locally due to database error',
        status: 'Novo lead',
        submission_date: new Date().toISOString()
      } as Lead;
    }

    console.log('✅ Lead insert request sent successfully (no data returned due to RLS).');
    
    // Send notification email (async, non-blocking)
    console.log('📧 Sending email notification for new lead...');
    supabase.functions.invoke('notify-new-lead', {
      body: { lead: leadData }
    }).then((response) => {
      if (response.error) {
        console.error('📧 Error sending notification:', response.error);
      } else {
        console.log('📧 Notification sent successfully:', response.data);
      }
    }).catch((err) => {
      console.error('📧 Failed to send notification:', err);
    });
    
    // Construct a client-side temporary ID for UI continuity
    const tempLead: Lead = {
      ...leadData,
      id: 'temp-' + Date.now(),
      notes: 'Lead salvo no servidor (ID não retornado por RLS)',
      status: 'Novo lead',
      submission_date: leadData.submission_date,
      source: 'formulario',
      origin_table: 'taxhealth_leads',
    };
    toast.success('✅ Dados enviados com sucesso!');
    return tempLead;
  } catch (error) {
    console.error('💥 CRITICAL ERROR in saveLead function:', error);
    console.error('💥 Error stack:', error instanceof Error ? error.stack : 'No stack trace');
    toast.error('⚠️ Erro crítico - dados serão salvos localmente');
    
    // Try to save locally as fallback
    try {
      console.log('💾 FALLBACK: Attempting to save lead locally after critical error');
      const { saveLead: saveLeadToLocalStorage } = await import('@/utils/localStorage');
      await saveLeadToLocalStorage(formData);
      toast.info('💾 Dados salvos localmente como backup');
    } catch (localError) {
      console.error('💥 FAILED to save locally:', localError);
      toast.error('❌ Falha crítica - não foi possível salvar dados');
    }
    
    // Even if there's an error, construct and return a lead object to continue the process
    return {
      ...formData,
      id: 'error-' + Date.now(),
      eligible: isEligible,
      emits_invoice: formData.emitsInvoice,
      monthly_revenue: formData.monthlyRevenue,
      tax_regime: formData.taxRegime,
      notes: 'Lead constructed after error',
      status: 'Novo lead',
      submission_date: new Date().toISOString()
    } as Lead;
  }
};
